import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { metaApi, ApiError } from '@/lib/api';
import type { BusinessProfileUpdatePayload } from '@/lib/api/services/meta';

export interface BusinessProfile {
  id?: string | null;
  about?: string | null;
  description?: string | null;
  email?: string | null;
  address?: string | null;
  vertical?: string | null;
  profile_picture_url?: string | null;
  profile_picture_handle?: string | null;
  websites: string[];
  whatsapp_connected?: boolean | null;
  reason?: string | null;
}

const BUSINESS_PROFILE_QUERY_KEY = (accountId: string) => ['business-profile', accountId];

const normalizeBusinessProfile = (payload: any): BusinessProfile => {
  if (!payload || typeof payload !== 'object') {
    return {
      id: null,
      about: null,
      description: null,
      email: null,
      address: null,
      vertical: null,
      profile_picture_url: null,
      profile_picture_handle: null,
      websites: [],
    };
  }

  if (payload.profile) {
    return normalizeBusinessProfile(payload.profile);
  }

  if (payload.business_profile) {
    return normalizeBusinessProfile(payload.business_profile);
  }

  if (Array.isArray(payload.data) && payload.data.length > 0) {
    return normalizeBusinessProfile(payload.data[0]);
  }

  if (payload.data && typeof payload.data === 'object') {
    return normalizeBusinessProfile(payload.data);
  }

  const source = payload;

  return {
    id: source?.id ?? null,
    about: source?.about ?? null,
    description: source?.description ?? null,
    email: source?.email ?? null,
    address: source?.address ?? null,
    vertical: source?.vertical ?? null,
    profile_picture_url: source?.profile_picture_url ?? null,
    profile_picture_handle: source?.profile_picture_handle ?? null,
    websites: Array.isArray(source?.websites)
      ? source.websites.filter((site: any) => typeof site === 'string' && site.trim().length > 0)
      : [],
  };
};

export const useBusinessProfile = (accountId?: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: BUSINESS_PROFILE_QUERY_KEY(accountId || ''),
    enabled: Boolean(accountId) && (options?.enabled ?? true),
    queryFn: async () => {
      if (!accountId) {
        throw new Error('Business account id is required');
      }

      const result = await metaApi.getBusinessProfile(accountId);

      if (result?.success === false) {
        throw ApiError.fromResult(result, 'Failed to fetch business profile');
      }

      const payload = result?.profile ?? result?.data ?? result;
      return {
        ...normalizeBusinessProfile(payload),
        whatsapp_connected:
          typeof result?.whatsapp_connected === 'boolean' ? result.whatsapp_connected : null,
        reason: typeof result?.reason === 'string' ? result.reason : null,
      };
    },
    retry: false,
  });
};

export const useUpdateBusinessProfile = (accountId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: BusinessProfileUpdatePayload) => {
      if (!accountId) {
        throw new Error('Business account id is required');
      }

      const result = await metaApi.updateBusinessProfile(accountId, updates);

      if (result?.success === false) {
        throw ApiError.fromResult(result, 'Failed to update business profile');
      }

      const payload = result?.profile ?? result?.data ?? result;
      return normalizeBusinessProfile(payload);
    },
    onSuccess: (profile) => {
      if (!accountId) return;
      queryClient.setQueryData(BUSINESS_PROFILE_QUERY_KEY(accountId), profile);
    },
  });
};

export const __private__ = {
  normalizeBusinessProfile,
};

